// Автодетект Яндекс.Метрики на странице ХОСТА и отправка целей воронки бота.
// Виджет — ЕДИНСТВЕННЫЙ источник этих целей: подхватывает любой счётчик на
// странице (и чужой сайт клиента, и наш ассииистент.рф — счётчик 110896711) и
// шлёт reachGoal напрямую. Нет Метрики / нет счётчиков — тихо молчим (у клиента
// её просто нет, это не ошибка). Клиент один раз заводит цели «JavaScript-событие»
// с id ниже (инструкция в кабинете); на нашем счётчике эти цели уже настроены.

// Внутренний action виджета (emitTrack) → id цели воронки сайт-чата в Метрике.
// Это те же site_chat_*, что уже заведены в нашем счётчике — id единые везде.
import { automationDetected } from './automation.ts';

const ACTION_GOALS: Record<string, string> = {
  opened: 'site_chat_opened',
  // Открытие ПО КЛИКУ гостя, отдельно от автооткрытия: site_chat_opened
  // засчитывается и когда чат раскрылся сам по тумблеру владельца, а это разный
  // интерес. Клик — намерение, автооткрытие — наша инициатива.
  'opened-by-user': 'site_chat_opened_by_user',
  // Закрытие окна ГОСТЕМ — отдельно от открытия: показывает, что человек
  // свернул чат сам, а не ушёл со страницы. Автозакрытий у нас нет, так что
  // событие всегда его действие.
  'closed-by-user': 'site_chat_closed_by_user',
  'message-sent': 'site_chat_message_sent',
  reply: 'site_chat_reply',
  lead: 'site_chat_lead',
};

// Все четыре цели — строго ОДИН РАЗ НА ПОСЕТИТЕЛЯ, а не на загрузку страницы.
// Отметка живёт в localStorage: держать её в памяти мало — посетитель обновляет
// страницу, снова открывает чат, и «открытие» уходило второй раз. Число
// достижений цели при этом было выше числа людей.
const SPENT_KEY_PREFIX = 'assiistent_goals_';

interface YaMetrika2 {
  /** Официальный реестр счётчиков страницы: массив объектов со свойством id. */
  counters?: () => Array<{ id?: number | string }>;
}

/** window.ym — вызываемая заглушка tag.js; до полной загрузки копит вызовы в
 *  очереди `.a`, где первый аргумент каждого вызова это counterId. */
type YmFunction = ((...args: unknown[]) => void) & { a?: unknown[][] };

interface MetrikaWindow extends Window {
  Ya?: { Metrika2?: YaMetrika2 };
  ym?: YmFunction;
}

function addId(ids: Set<number>, raw: unknown): void {
  const id = Number(raw);
  if (Number.isFinite(id) && id > 0) ids.add(id);
}

/** Все счётчики Метрики на странице хоста. Надёжный путь — Ya.Metrika2.counters()
 *  (после загрузки tag.js). Фолбэк на раннем этапе — очередь window.ym.a, где
 *  первый аргумент каждого вызова это counterId. Дубли и мусор отсеиваем. */
export function detectCounterIds(win: MetrikaWindow): number[] {
  const ids = new Set<number>();
  const metrika2 = win.Ya?.Metrika2;
  if (typeof metrika2?.counters === 'function') {
    for (const counter of metrika2.counters() ?? []) addId(ids, counter?.id);
  }
  if (ids.size === 0 && typeof win.ym === 'function') {
    for (const call of win.ym.a ?? []) addId(ids, call?.[0]);
  }
  return [...ids];
}

/** ClientID посетителя у счётчика хозяина страницы. Нужен серверу, чтобы
 *  привязать ОФЛАЙН-конверсию по заявке к тому же человеку: «лид оказался
 *  настоящим» решает менеджер в кабинете спустя часы, когда посетителя на
 *  сайте давно нет и reachGoal слать некому (Leads::Conversion).
 *
 *  Берём у ПЕРВОГО счётчика: их на странице бывает несколько, но кука
 *  `_ym_uid` одна на домен — значение у всех одинаковое. Нет Метрики, нет
 *  счётчиков, старый tag.js без getClientID — молча ничего не отдаём: без
 *  ClientID работает всё, кроме офлайн-конверсий.
 *
 *  Номер счётчика отдаём ВМЕСТЕ с ClientID: сервер шлёт хит в этот счётчик
 *  именно с этим ClientID, и разъехавшаяся пара увела бы конверсию туда, где
 *  такого посетителя не было (Metrika::Counter).
 *
 *  Но НАЙДЕННЫЙ номер сообщаем отдельным колбэком и сразу — не дожидаясь
 *  ответа getClientID. До 18.08.2026 номер ехал только в паре, поэтому у
 *  посетителя, чей счётчик так и не отдал ClientID (куки-баннер, блокировщик,
 *  приватный режим), сервер получал `metrika_counter_id: 0` — и карточка
 *  сотрудника оставалась без счётчика, хотя тот на странице есть. Проверено на
 *  39плит.рф: ClientID был у 2 диалогов из 5, номер счётчика — ни у одного.
 *  Расхождения пары это не создаёт: и номер, и ключ берутся у ПЕРВОГО счётчика
 *  страницы, а `_ym_uid` на домене один на все счётчики. */
export function requestClientId(
  win: MetrikaWindow,
  done: (clientId: string, counterId: number) => void,
  onCounter?: (counterId: number) => void,
): void {
  const ym = win.ym;
  const [id] = detectCounterIds(win);
  if (typeof ym !== 'function' || id === undefined) return;

  onCounter?.(id);
  try {
    ym(id, 'getClientID', (clientId: unknown) => {
      if (typeof clientId === 'string' && clientId !== '') done(clientId, id);
    });
  } catch {
    /* счётчик хоста сломан — не наша забота, офлайн-конверсии просто не будет */
  }
}

/** Пауза между попытками достучаться до счётчика и срок, после которого
 *  перестаём: минуты хватает и медленной сети, и куки-баннеру, а вечный опрос
 *  на странице без Метрики — трата батареи впустую. */
const CLIENT_ID_RETRY_MS = 1000;
const CLIENT_ID_GIVE_UP_MS = 60_000;

/** Дождаться ClientID, даже если Метрика появится ПОЗЖЕ виджета.
 *
 *  Один вопрос в момент старта работает только там, где сниппет счётчика стоит
 *  выше нашего тега (маркетпульт.рф). Но счётчик часто подключают отложенно —
 *  по первому действию, после согласия на куки (так на нашем же лендинге), из
 *  тег-менеджера. Тогда в момент старта `window.ym` ещё не существует,
 *  requestClientId молча выходит, и ClientID не появляется НИКОГДА: офлайн-
 *  конверсия по заявке (Leads::Conversion) и цель молчуна
 *  (Conversations::SilentVisitor) отваливаются целиком.
 *
 *  Поэтому переспрашиваем раз в секунду, пока счётчик не ответит.
 *
 *  @param schedule планировщик виджета (Timers.after): свои таймеры виджет
 *    заводит только через реестр, чтобы stop() гасил и этот опрос тоже. */
export function watchClientId(
  win: MetrikaWindow,
  schedule: (delay: number, fn: () => void) => void,
  done: (clientId: string, counterId: number) => void,
  options: {
    retryMs?: number;
    giveUpMs?: number;
    now?: () => number;
    onCounter?: (counterId: number) => void;
  } = {},
): void {
  const retryMs = options.retryMs ?? CLIENT_ID_RETRY_MS;
  const now = options.now ?? (() => Date.now());
  const deadline = now() + (options.giveUpMs ?? CLIENT_ID_GIVE_UP_MS);
  let answered = false;
  let counterSeen = false;

  const attempt = (): void => {
    if (answered) return;

    const reportCounter = (counterId: number): void => {
      // Номер счётчика не меняется от попытки к попытке — сообщаем один раз.
      if (counterSeen) return;

      counterSeen = true;
      options.onCounter?.(counterId);
    };

    requestClientId(
      win,
      (clientId, counterId) => {
        // Ответ счётчика асинхронный (вызов лежит в очереди ym до загрузки
        // tag.js), поэтому за время ожидания могла уйти ещё попытка — берём
        // первый ответ и гасим остальные.
        if (answered) return;

        answered = true;
        done(clientId, counterId);
      },
      reportCounter,
    );
    if (!answered && now() < deadline) schedule(retryMs, attempt);
  };

  attempt();
}

/** Внутренний action → цель Метрики (или null, если события нет в воронке). */
export function goalForAction(action: string): string | null {
  return ACTION_GOALS[action] ?? null;
}

/** Ключ отметки достигнутых целей. Свой на каждый виджет: на одном браузере
 *  может быть открыт и наш сайт, и сайт клиента — цели у них разные. */
export function spentKey(publicKey: string): string {
  return `${SPENT_KEY_PREFIX}${publicKey}`;
}

/** Профильный ответ ассистента — ПРЕДПОСЫЛКА цели site_chat_reply, а не сама
 *  цель. Сервер ставит on_topic лишь при topic=='profile' (spec §5);
 *  оффтоп/абьюз/забытый маркер → флага нет.
 *
 *  Цель называется «посетитель ответил боту» и в отчётах стоит дороже
 *  message_sent, потому что отмечает ЗАВЯЗАВШИЙСЯ диалог. Раньше она уходила
 *  прямо здесь — на ответе БОТА, то есть почти каждому, кто написал хоть что-то
 *  и получил осмысленный ответ. Это дублировало site_chat_message_sent и
 *  завышало конверсию: в ставках цель ценнее, а срабатывала она на том же
 *  событии. Теперь ответ бота только ВЗВОДИТ предпосылку, а цель уходит на
 *  следующей реплике посетителя (shouldTrackVisitorReply). */
export function marksOnTopicAnswer(message: { role?: string; on_topic?: boolean }): boolean {
  return message.role !== 'visitor' && message.on_topic === true;
}

/** Гейт цели site_chat_reply: посетитель пишет снова, уже прочитав профильный
 *  ответ бота. Обе части обязательны — без первой цель уходила бы на любом
 *  сообщении, без второй считала бы диалог завязавшимся по реплике самого бота. */
export function shouldTrackVisitorReply(botAnsweredOnTopic: boolean, senderIsVisitor: boolean): boolean {
  return botAnsweredOnTopic && senderIsVisitor;
}

/** Достигнутые цели из localStorage. Приватный режим, переполнение, мусор в
 *  ключе — всё это не повод ломать виджет: считаем, что целей ещё не было. */
function loadSpent(win: MetrikaWindow, key: string): Set<string> {
  try {
    const raw = win.localStorage?.getItem(key);
    const list: unknown = raw ? JSON.parse(raw) : [];
    return new Set(
      Array.isArray(list) ? list.filter((item): item is string => typeof item === 'string') : [],
    );
  } catch {
    return new Set();
  }
}

function saveSpent(win: MetrikaWindow, key: string, spent: Set<string>): void {
  try {
    win.localStorage?.setItem(key, JSON.stringify([...spent]));
  } catch {
    /* писать некуда — цели просто отправятся ещё раз на следующей загрузке */
  }
}

/** Отправитель целей: на каждое событие воронки находит счётчики хоста и шлёт
 *  reachGoal во ВСЕ (у клиента обычно один; несколько — получат все).
 *
 *  Каждая цель уходит РОВНО ОДИН РАЗ на посетителя: отметка переживает
 *  перезагрузку страницы (localStorage), иначе повторный заход задваивал бы
 *  достижения и число целей превышало бы число людей.
 *
 *  Отмечаем только ПОСЛЕ успешной отправки: tag.js Метрики грузится асинхронно,
 *  и на раннем открытии чата счётчика может ещё не быть. Отметь мы цель до
 *  проверки — она пропала бы навсегда.
 *
 *  Нет ym / нет счётчиков — тихо пропускаем (у клиента просто нет Метрики).
 *
 *  Автоматизация (webdriver/CDP) целей не шлёт вовсе: site_chat_* уходят в
 *  счётчик клиента и участвуют в ставках Директа, а кликеру открыть чат и
 *  написать реплику ничего не стоит (17.08.2026). */
export function createMetrikaReporter(win: MetrikaWindow = window, publicKey = ''): (action: string) => void {
  const key = spentKey(publicKey);
  const spent = loadSpent(win, key);
  return (action) => {
    if (automationDetected(win as never)) return;

    const goal = goalForAction(action);
    if (!goal || spent.has(action)) return;

    const ym = win.ym;
    if (typeof ym !== 'function') return;
    const ids = detectCounterIds(win);
    if (ids.length === 0) return;

    ids.forEach((id) => {
      try {
        ym(id, 'reachGoal', goal);
      } catch {
        /* один сломанный счётчик хоста не должен мешать остальным */
      }
    });
    spent.add(action);
    saveSpent(win, key, spent);
  };
}
